const endpoints = {
  ubt: {
    profile: {
      getProfile: "/ubt/profile",
    },
    myTeams: {
      myTeamsList: "/ubt/my-teams",
      deleteTeam: (id: number) => `/ubt/my-teams/${id}`,
    },
    members: {
      getTeamMembers: (teamId?: number | string) =>
        `/ubt/teams/${teamId ?? 2}/aggregated`,
      getDefaultMembers: (
        teamId?: number | string,
        params?: {
          sortColumn?: string;
          sortOrder?: string;
          page?: number;
          per_page?: number;
        },
      ) => {
        const queryParams = new URLSearchParams();
        if (params?.sortColumn)
          queryParams.append("sortColumn", params.sortColumn);
        if (params?.sortOrder)
          queryParams.append("sortOrder", params.sortOrder);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.per_page)
          queryParams.append("per_page", params.per_page.toString());
        const queryString = queryParams.toString();
        const query = queryString ? `?${queryString}` : "";
        return `/ubt/teams/${teamId}/members/default${query}`;
      },
      getManualMembers: (
        teamId?: number | string,
        params?: {
          sortColumn?: string;
          sortOrder?: string;
          page?: number;
          per_page?: number;
        },
      ) => {
        const queryParams = new URLSearchParams();
        if (params?.sortColumn)
          queryParams.append("sortColumn", params.sortColumn);
        if (params?.sortOrder)
          queryParams.append("sortOrder", params.sortOrder);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.per_page)
          queryParams.append("per_page", params.per_page.toString());
        const queryString = queryParams.toString();
        const query = queryString ? `?${queryString}` : "";
        return `/ubt/teams/${teamId}/members/manual${query}`;
      },
      getMembershipSummary: (
        teamId?: number | string,
        defaultMembership?: string,
      ) => {
        const queryParam = defaultMembership
          ? `?defaultMembership=${defaultMembership}`
          : "";
        return `/ubt/teams/${teamId}/membership${queryParam}`;
      },
      getMemberAccess: (teamId?: number | string) =>
        `/ubt/member/access/${encodeURIComponent(String(teamId ?? ""))}`,
      // Returns available team-role options for members. Accepts query param `member=default|manual`
      getTeamMemberRoleStatus: (member?: string) => {
        const q = member ? `?member=${encodeURIComponent(member)}` : "";
        return `/ubt/team-member-role-status${q}`;
      },
      // PUT — update a defaulted team member's role/status
      updateTeamMemberStatus: "/ubt/team-member/status",
      // PUT — update a manually-added team member's role/status
      updateManualTeamMemberStatus: "/ubt/manual-team-member/status",
      // POST — add a member to a team
      addTeamMember: "/ubt/team-member",
      // GET — search team members by nuid or name
      searchTeamMembers: (params: {
        searchBy: "nuid" | "name";
        teamId: number | string;
        defaultMembership?: string;
        nuid?: string;
        firstName?: string;
        lastName?: string;
        sortColumn?: string;
        sortOrder?: string;
        page?: number;
        per_page?: number;
      }) => {
        const q = new URLSearchParams();
        q.append("searchBy", params.searchBy);
        if (params.nuid) q.append("nuid", params.nuid);
        if (params.firstName) q.append("firstName", params.firstName);
        if (params.lastName) q.append("lastName", params.lastName);
        q.append("teamId", String(params.teamId));
        if (params.defaultMembership)
          q.append("defaultMembership", params.defaultMembership);
        if (params.sortColumn) q.append("sortColumn", params.sortColumn);
        if (params.sortOrder) q.append("sortOrder", params.sortOrder);
        if (params.page) q.append("page", String(params.page));
        if (params.per_page) q.append("per_page", String(params.per_page));
        return `/ubt/team-members/search?${q.toString()}`;
      },
    },
    history: {
      historyUBTList: (teamId?: number | string) =>
        `/ubt/teams/${teamId ?? null}/history`,
    },
    costCenter: {
      costCenterUBTList: "/ubt/costCenter/costCenter-list",
      teamRatingOptions: "/ubt/likert-scale-rating",
      serviceTypeOptions: "/ubt/service-delivery-type",
      shiftOptions: "/ubt/shift-options",
      createUBTTeam: "/ubt/create",
      updateUBTTeam: (teamId: number | string) => `/ubt/edit/${teamId}`,
      addCostCenter: (teamId: number | string) =>
        `/ubt/edit/${teamId}/cost-center/add`,
      updateCostCenter: (teamId: number | string) =>
        `/ubt/edit/${teamId}/cost-center/edit`,
      deleteCostCenter: (teamId: number | string) =>
        `/ubt/edit/${teamId}/cost-center/delete`,
      getCostCenterDropdowns: ({
        regionCd,
        areaCd,
        locationCd,
        entityCd,
        centerCd,
      }: {
        regionCd: string;
        areaCd: string;
        locationCd: string;
        entityCd: string;
        centerCd: string;
      }) => {
        const q = new URLSearchParams({
          regionCd,
          areaCd,
          locationCd,
          entityCd,
          centerCd,
        });
        return `/ubt/add-edit/cost-center?${q.toString()}`;
      },
    },
    teams: {
      getTeamDetails: (teamId: number | string) =>
        `/ubt/teams/${encodeURIComponent(String(teamId))}`,
      getUBTTeam: (teamId: number | string) =>
        `/ubt/${encodeURIComponent(String(teamId))}`,
    },
    selectUBT: {
      getSelectUBTList: "/ubt/teams/search",
    },
    teamsReporting: {
      teamsReportingList: "/ubt/teams-reporting/team-ubt-list",
      ratingOptions: "/ubt/teams-reporting/rating-options",
      rolesOptions: "/ubt/teams-reporting/roles-options",
    },
    teamsReport: {
      teamsSearch: "/ubt/teams-reporting/teams-search",
      getRatingRoles: "/ubt/teams-reporting/rating-roles",
      getLeadsDropdown: "/ubt/teams-reporting/leads-dropdown",
      downloadPDF: "/ubt/reports/teams-reporting/pdf",
      downloadTeamsReportingCSV: "/ubt/reports/teams-reporting",
      downloadP2PDetailPDF: "/ubt/reports/teams-reporting/p2p-details/pdf",
      downloadP2PDetailCSV: "/ubt/reports/teams-reporting/p2p-details",
      membershipCountCSV: "/ubt/report/ubt-membership-count",
      privilegeRolesByCriteria: "/ubt/reports/privilege-role/by-criteria",
    },
    reports: {
      testSeries: "/ubt/reports/test-series",
      displaySummaryAndDetails: "/ubt/reports/display-summary-and-details",
      exportGLDataCSV: "/ubt/report/gl-data",
      /** GL data export for the My Teams page — filters by the user's team IDs on the back-end. */
      exportGLDataByTeamIds: "/ubt/report/gl-data/by-team-ids",
      exportDetailsByCriteria: "/ubt/reports/details/by-criteria",
      exportDetailsByTeams: "/ubt/reports/details/by-teams",
      testOfChangeReport: "/ubt/reports/test-of-change",
      membersRoles: "/ubt/reports/members-roles",
      pipHistory: "/ubt/reports/pip-history",
      pipDetails: "/ubt/reports/pip-details",
      projectRecordPdf: (perfId: string | number): string =>
        `/ubt/reports/project-record-pdf/${encodeURIComponent(String(perfId))}`,
      /**
       * Returns the URL for the project test-of-change HTML report endpoint.
       * Query params (sortColumn, sortOrder, projectName) are passed separately.
       */
      projectTestOfChangePdf: (projectId: string | number): string =>
        `/ubt/reports/project/${encodeURIComponent(String(projectId))}/test-of-change`,
    },
    editUBT: {
      fetchEditUBTList: "/ubt/edit-ubt/edit-ubt-list",
    },
    project: {
      fetchProjectList: "/ubt/search-project/projects",
      searchProject: (regionCode: string) =>
        `/ubt/${regionCode}/search-project`,
    },
    employee: {
      getEmployeeDetails: "/ubt/employee",
    },
    projectList: {
      getpipList: (teamId?: number | string) =>
        `/ubt/project/${teamId ?? null}/getPipListServlet`,
    },
    projectDetails: {
      getpipDetails: (pipID: number) => `/ubt/project/${pipID}`,
      deletepip: (pipID: number) => `/ubt/project/${pipID}`,
      deleteProjectTeam: (teamId: number | string) =>
        `/ubt/project/team/${encodeURIComponent(String(teamId))}`,
      getProjectAccess: (teamId: number | string) =>
        `/ubt/project/access/${encodeURIComponent(String(teamId))}`,
      getTeamTrackedStatus: (teamId: number | string) =>
        `/ubt/my-teams/${encodeURIComponent(String(teamId))}/is-tracked`,
      addTeamToMyTeams: "/ubt/my-teams/add",
    },
    manageProject: {
      getfocusAreaValueCompass: (
        projectId: number | string,
        focusId: number | string,
        nuid?: string | null,
      ) => {
        const q = nuid
          ? `?operation=getvaluecompass&focusid=${encodeURIComponent(String(focusId))}&nuid=${encodeURIComponent(String(nuid))}`
          : `?operation=getvaluecompass&focusid=${encodeURIComponent(String(focusId))}`;
        return `/ubt/projects/${encodeURIComponent(String(projectId))}/getFocusAreaValueCompass${q}`;
      },
    },
    testsOfChange: {
      getTestsOfChange: (pipID: number) =>
        `/ubt/project/${pipID}/tests-of-change`,
      addTestOfChange: (pipID: number) =>
        `/ubt/project/${pipID}/tests-of-change`,
      deleteTestOfChange: (pipID: number, tocID: number) =>
        `/ubt/project/${pipID}/tests-of-change/${tocID}`,
      updateTestOfChange: (pipID: number, tocID: number) =>
        `/ubt/project/${pipID}/tests-of-change/${tocID}`,
      fetchIdeaSourcesRating: "/ubt/projects/getRatingAndSource",
      fetchShiftLookup: "/ubt/projects/getShiftLookup",
    },
    testDataSeries: {
      getTestDataSeries: (pipID: number) =>
        `/ubt/project/${pipID}/test-data-series`,
      addTestDataSeries: (tocID: number) =>
        `/ubt/project/${tocID}/test-data-series`,
      deleteTestDataSeries: (tocID: number, seriesId: number) =>
        `/ubt/project/${tocID}/test-data-series/${seriesId}`,
      updateTestDataSeries: (tocID: number, seriesId: number) =>
        `/ubt/project/${tocID}/test-data-series/${seriesId}`,
    },
    locations: {
      getUBTLocations: (locationType?: string) => {
        const queryParam = locationType ? `?locationType=${locationType}` : "";
        return `/ubt/locations${queryParam}`;
      },
    },
    manageAccess: {
      getUsersList: "/ubt/manage-access/user",
      addUser: "/ubt/manage-access/user",
      // Search users by region (+ optional name/NUID/pagination/sort params)
      searchUsers: ({
        region,
        fName,
        lName,
        searchKey,
        value,
        sortBy,
        sortOrder,
        page,
        perPage,
      }: {
        region: string;
        fName?: string;
        lName?: string;
        searchKey?: string;
        value?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        perPage?: number;
      }) => {
        let q = `?region=${encodeURIComponent(region)}`;
        if (searchKey)
          q += `&searchKey=${encodeURIComponent(searchKey.trim())}`;
        if (fName) q += `&firstName=${encodeURIComponent(fName.trim())}`;
        if (lName) q += `&lastName=${encodeURIComponent(lName.trim())}`;
        if (value) q += `&value=${encodeURIComponent(value.trim())}`;
        if (perPage) q += `&per_page=${perPage}`;
        if (page) q += `&page=${page}`;
        if (sortBy) q += `&sortBy=${encodeURIComponent(sortBy)}`;
        if (sortOrder) q += `&sortOrder=${encodeURIComponent(sortOrder)}`;
        return `/ubt/manage-access/user/search${q}`;
      },
      // Edit user access
      editAccess: "/ubt/manage-access/edit",
      // Delete user access
      deleteUser: "/ubt/manage-access/user/delete",
      // Returns available access actions / role options for a user. Accepts optional `nuid` query param.
      getAccessActions: (nuid?: string) => {
        const q = nuid ? `?nuid=${encodeURIComponent(nuid)}` : "";
        return `/ubt/manage-access/user/access-actions${q}`;
      },
    },
    pathToPerformance: {
      // Check authorization level for a team
      getAuthorization: (teamId: number | string) => `/ubt/p2p/auth/${teamId}`,
      // Get category levels and action needed options
      getCategoryLevels: "/ubt/p2p/catg-level",
      // Get P2P data for a team
      getP2PData: (teamId: number | string) => `/ubt/p2p/${teamId}`,
      // Create new P2P record
      createP2P: "/ubt/p2p",
      // Update existing P2P record
      updateP2P: "/ubt/p2p",
      // Update team settings (learning plan, huddles)
      updateTeamSettings: "/ubt/p2p/team/settings",
    },
    contact: {
      // Get contact details (NUID, first name, last name, email)
      getContactDetails: "/ubt/contact/details",
      // Get contact locations based on loadValue (CENTER or FEEDBACK_TYPE)
      getContactLocations: (
        loadValue: string,
        regionCode?: string,
        centerCode?: string,
      ) => {
        const q = new URLSearchParams();
        q.append("loadValue", loadValue);
        if (regionCode) q.append("regionCode", regionCode);
        if (centerCode) q.append("centerCode", centerCode);
        return `/ubt/contact/locations?${q.toString()}`;
      },
      // Send contact us message
      sendContactMessage: "/ubt/contact/send",
    },
    teamDocuments: {
      // Get list of projects for a team
      getTeamProjects: (teamId: number | string) =>
        `/ubt/teams-documents/teamId/${teamId}/projects`,
      // Get project details by projectId (perfId)
      getProjectDetails: (projectId: number | string) =>
        `/ubt/project/${projectId}`,
      // Get documents for a specific project in a team
      getProjectDocuments: (
        teamId: number | string,
        projectId: number | string,
      ) => `/ubt/teams-documents/teamId/${teamId}/projects/${projectId}`,
      // Get all project documents for a team
      getAllProjectDocuments: (teamId: number | string) =>
        `/ubt/teams-documents/all-projects/teamId/${teamId}`,
      // Get team level documents
      getTeamLevelDocuments: (teamId: number | string) =>
        `/ubt/teams-documents/teamId/${teamId}`,
      // Download a document
      downloadDocument: (
        type: "team" | "project",
        typeId: number | string,
        documentId: number | string,
      ) =>
        `/ubt/teams-documents/type/${type}/typeId/${typeId}/documentId/${documentId}`,
      // Upload a document
      uploadDocument: (type: "team" | "project", typeId: number | string) =>
        `/ubt/teams-documents/type/${type}/typeId/${typeId}`,
      // Delete a document
      deleteDocument: (id: number | string) =>
        `/ubt/teams-documents/type/document/id/${id}`,
    },
  },
};

export default endpoints;
